import { useLocalStorage } from '@mantine/hooks'
import type { PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IntercomProvider, useIntercom } from 'react-use-intercom'
import { useMyInfo } from 'src/services/apollo/auth'
import { useLocation } from 'src/utils/router/route'
import { config } from '../config'
import { INTERCOM_USER_JWT_KEY } from './storage'

interface IntercomContextType {
  showing: boolean
  unreadCount: number
}

const IntercomContext = createContext<IntercomContextType>({
  showing: false,
  unreadCount: 0,
})

export function IntercomController() {
  const intercom = useIntercom()
  const location = useLocation()
  const { showing, unreadCount } = useContext(IntercomContext)
  useEffect(() => {
    intercom.update({
      hideDefaultLauncher: !(
        location.pathname.endsWith('/feedback') ||
        showing ||
        unreadCount > 0
      ),
    })
  }, [intercom, location, showing, unreadCount])

  const { i18n } = useTranslation()
  useEffect(() => {
    const language = i18n.language
    const languageOverride = language === 'zh' ? 'zh-tw' : language
    intercom.update({
      languageOverride,
    })
  }, [i18n.language, intercom])

  const [intercomUserJwt] = useLocalStorage({ key: INTERCOM_USER_JWT_KEY })
  const { data: userInfo } = useMyInfo()
  useEffect(() => {
    if (intercomUserJwt && userInfo) {
      intercom.update({
        email: userInfo?.email,
        userId: userInfo?.id,
        createdAt: userInfo?.createdAt,
        name: userInfo?.displayName || userInfo?.username,
        intercomUserJwt,
      })
    }
  }, [intercom, intercomUserJwt, userInfo])
  return null
}

export function IntercomMessengerProvider({ children }: PropsWithChildren) {
  const [showing, setShowing] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const value = { showing, unreadCount }
  return (
    <IntercomProvider
      appId={config.intercomAppId}
      initializeDelay={1000}
      autoBoot
      onShow={() => {
        setShowing(true)
      }}
      onHide={() => {
        setShowing(false)
      }}
      onUnreadCountChange={setUnreadCount}
    >
      <IntercomContext.Provider value={value}>
        {children}
      </IntercomContext.Provider>
    </IntercomProvider>
  )
}
